import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import type { ReportingScope } from "@packages/core/organization/scope.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/field.tsx";
import { Modal, ModalBody, ModalClose, ModalContent, ModalFooter, ModalHeader, ModalTitle } from "@/components/ui/modal.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { TextField } from "@/components/ui/text-field.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { useEngagementClient, useSendSurveyReminderMutationOptions } from "../api/query.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

export interface SendSurveyReminderDialogProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    scope: ReportingScope;
}

export function SendSurveyReminderDialog({ isOpen, onOpenChange, scope }: SendSurveyReminderDialogProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("SendSurveyReminderDialog", { i18n: i18nextInstance });

    const client = useEngagementClient();
    const { data, isPending: isCountPending, isError: isCountError } = useQuery(createQueryOptions(client.getSurveyReminderAudienceCount.queryOptions({ input: { scope } }), { enabled: isOpen, throwOnError: false }));
    const isCountLoading = useLoadingIndicator(isCountPending);

    const [customMessage, setCustomMessage] = useState("");

    const { mutate: sendSurveyReminder, isPending: isSending } = useMutation(useSendSurveyReminderMutationOptions());

    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            if (!nextIsOpen) {
                setCustomMessage("");
            }
            onOpenChange(nextIsOpen);
        },
        [onOpenChange]
    );

    const handleSend = useCallback(() => {
        sendSurveyReminder(
            { scope, customMessage: customMessage.trim() || undefined },
            {
                onSuccess: () => {
                    toast.success(t("successToast"), { position: "top-right" });
                    handleOpenChange(false);
                },
                onError: () => toast.error(t("errorToast"), { position: "top-right" })
            }
        );
    }, [sendSurveyReminder, scope, customMessage, t, handleOpenChange]);

    const countMessage = isCountError ? t("countUnavailable") : t("count", { count: data?.inactiveMemberCount ?? 0 });
    const isSendDisabled = !isCountPending && !isCountError && data?.inactiveMemberCount === 0;

    return (
        <Modal isOpen={isOpen} onOpenChange={handleOpenChange}>
            <ModalContent size="md" isDismissable={!isSending}>
                <ModalHeader>
                    <ModalTitle>{t("title")}</ModalTitle>
                </ModalHeader>
                <ModalBody className="flex flex-col gap-4">
                    <output className="sr-only">{isCountLoading && t("countLoading")}</output>
                    <Skeleton isLoading={isCountLoading} inert={isCountLoading}>
                        <div>
                            <div>{isCountLoading ? <div className="h-4 w-40" /> : <p className="text-muted-fg text-sm">{countMessage}</p>}</div>
                        </div>
                    </Skeleton>
                    <TextField value={customMessage} onChange={setCustomMessage} className="flex flex-col">
                        <Label>{t("messageLabel")}</Label>
                        <Textarea placeholder={t("messagePlaceholder")} />
                    </TextField>
                </ModalBody>
                <ModalFooter>
                    <ModalClose intent="plain" isDisabled={isSending}>
                        {t("cancel")}
                    </ModalClose>
                    <Button intent="primary" onPress={handleSend} isPending={isSending} isDisabled={isSendDisabled}>
                        {t("send")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
